import {
  useState,
  createContext,
  useCallback,
  useMemo,
  type FC,
  type ReactNode,
} from 'react'

export type SectionName =
  | 'JourneyStart'
  | 'ForTheEye'
  | 'ForThePure'
  | 'ForTheFeeling'
  | 'ForTheTaste'
  | 'ForTheRide'
  | 'ForTheFill'
  | 'ForTheControl'
  | 'ForTheFlow'
  | 'JourneyEnd'

export type Section = {
  name: SectionName
  orderNumber: number
}

export type SetCurrentSection = (sectionName: SectionName) => void

type CurrentSectionContextState = {
  currentSection: Section
  setCurrentSection: SetCurrentSection
}

const initialSection: Section = {
  name: 'JourneyStart',
  orderNumber: 0,
}

export const SECTIONS: Section[] = [
  {
    name: 'JourneyStart',
    orderNumber: 0,
  },
  {
    name: 'ForTheEye',
    orderNumber: 1,
  },
  {
    name: 'ForThePure',
    orderNumber: 2,
  },
  {
    name: 'ForTheFeeling',
    orderNumber: 3,
  },
  {
    name: 'ForTheTaste',
    orderNumber: 4,
  },
  {
    name: 'ForTheRide',
    orderNumber: 5,
  },
  {
    name: 'ForTheFill',
    orderNumber: 6,
  },
  {
    name: 'ForTheControl',
    orderNumber: 7,
  },
  {
    name: 'ForTheFlow',
    orderNumber: 8,
  },
  {
    name: 'JourneyEnd',
    orderNumber: 9,
  },
]

export const CurrentSectionContext = createContext<CurrentSectionContextState>({
  currentSection: initialSection,
  setCurrentSection: (_sectionName: SectionName) => {},
})

const CurrentSectionProvider: FC<{
  children?: ReactNode
}> = ({ children }) => {
  const [currentSection, setCurrentSection] = useState<Section>(initialSection)

  const handleSetCurrentSection = useCallback((sectionName: SectionName) => {
    const section = SECTIONS.find((section) => section.name === sectionName)

    if (!section) return

    setCurrentSection(section)
  }, [])

  const context = useMemo(
    () => ({
      currentSection,
      setCurrentSection: handleSetCurrentSection,
    }),
    [currentSection, handleSetCurrentSection]
  )

  return (
    <CurrentSectionContext.Provider value={context}>
      {children}
    </CurrentSectionContext.Provider>
  )
}

export default CurrentSectionProvider
